"use client"

import { useState, useEffect, useRef } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"

interface Banner {
  id: string
  title: string
  subtitle?: string
  description?: string
  image: string
  images?: string // JSON array of image URLs
  mobileImage?: string
  linkUrl?: string
  linkText?: string
  openInNewTab: boolean
  alignment?: 'center' | 'left' | 'right'
}

interface BannerDisplayProps {
  position?: string
  device?: string
  audience?: string
  className?: string
}

export function BannerDisplay({
  position = "homepage",
  device = "all",
  audience = "all",
  className = ""
}: BannerDisplayProps) {
  const [banners, setBanners] = useState<Banner[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([])
  // For image carousel within a banner
  const [imageIndexes, setImageIndexes] = useState<{ [bannerId: string]: number }>({})
  const [isHovered, setIsHovered] = useState(false)
  const autoSlideRef = useRef<NodeJS.Timeout | null>(null)
  // --- Add swipe/drag state ---
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const mouseDownX = useRef<number | null>(null)
  const mouseMoveX = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Move visibleBanners up so it is available for hooks below
  const visibleBanners = banners.filter(banner => !dismissedBanners.includes(banner.id))

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const params = new URLSearchParams({
          position,
          device,
          audience,
        })
        const response = await fetch(`/api/banners?${params}`)
        if (!response.ok) {
          throw new Error(`Failed to fetch banners: ${response.status}`)
        }
        const data = await response.json()
        setBanners(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load banners')
      } finally {
        setLoading(false)
      }
    }
    fetchBanners()
  }, [position, device, audience])

  const handleDismiss = (bannerId: string) => {
    setDismissedBanners(prev => [...prev, bannerId])
  }

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? banners.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex(prev => (prev === banners.length - 1 ? 0 : prev + 1))
  }

  // For image carousel within a banner
  const handleImagePrev = (bannerId: string, images: string[]) => {
    setImageIndexes(prev => ({
      ...prev,
      [bannerId]: prev[bannerId] === 0 ? images.length - 1 : (prev[bannerId] || 0) - 1
    }))
  }
  const handleImageNext = (bannerId: string, images: string[]) => {
    setImageIndexes(prev => ({
      ...prev,
      [bannerId]: prev[bannerId] === images.length - 1 ? 0 : (prev[bannerId] || 0) + 1
    }))
  }

  // --- Banner auto-slide ---
  useEffect(() => {
    if (loading) return
    if (isHovered) return
    if (visibleBanners.length <= 1) return
    autoSlideRef.current && clearInterval(autoSlideRef.current)
    autoSlideRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev === visibleBanners.length - 1 ? 0 : prev + 1))
    }, 4000)
    return () => {
      autoSlideRef.current && clearInterval(autoSlideRef.current)
    }
  }, [visibleBanners.length, loading, isHovered])

  // --- Touch/drag handlers for banners ---
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX
  }
  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current
      if (diff > 50) setCurrentIndex(prev => (prev === visibleBanners.length - 1 ? 0 : prev + 1))
      else if (diff < -50) setCurrentIndex(prev => (prev === 0 ? visibleBanners.length - 1 : prev - 1))
    }
    touchStartX.current = null
    touchEndX.current = null
  }
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    mouseDownX.current = e.clientX
  }
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    mouseMoveX.current = e.clientX
  }
  const handleMouseUp = () => {
    if (isDragging && mouseDownX.current !== null && mouseMoveX.current !== null) {
      const diff = mouseDownX.current - mouseMoveX.current
      if (diff > 50) setCurrentIndex(prev => (prev === visibleBanners.length - 1 ? 0 : prev + 1))
      else if (diff < -50) setCurrentIndex(prev => (prev === 0 ? visibleBanners.length - 1 : prev - 1))
    }
    setIsDragging(false)
    mouseDownX.current = null
    mouseMoveX.current = null
  }

  if (loading) {
    return (
      <div className="bg-muted/30 border rounded-lg p-8 text-center">
        <p>Loading banners...</p>
      </div>
    )
  }

  if (error) {
    return null // Silently fail for non-critical banner component
  }

  if (visibleBanners.length === 0) {
    return (
      <div className="bg-muted/30 border rounded-lg p-8 text-center">
        <p>No banners found for position: {position}</p>
        <p className="text-sm text-muted-foreground">Total banners: {banners.length}</p>
      </div>
    )
  }

  const currentBanner = visibleBanners[currentIndex]
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
  // Parse images array if present
  let imagesArr: string[] = []
  if (currentBanner.images) {
    try {
      imagesArr = JSON.parse(currentBanner.images)
    } catch {}
  }
  // Fallback to single image if no images array
  if (!imagesArr || imagesArr.length === 0) {
    // Use placehold.co with responsive sizes
    const placeholderUrl = `https://placehold.co/1200x520/6366f1/ffffff?text=1200x520`
    imagesArr = [isMobile && currentBanner.mobileImage ? currentBanner.mobileImage : (currentBanner.image || placeholderUrl)]
  }
  const imageIndex = imageIndexes[currentBanner.id] || 0
  const imageUrl = imagesArr[imageIndex]
  const alignmentClass = currentBanner.alignment === 'left' ? 'items-start text-left' : currentBanner.alignment === 'right' ? 'items-end text-right' : 'items-center text-center'

  const handleBannerClick = () => {
    if (currentBanner.linkUrl) {
      if (currentBanner.openInNewTab) {
        window.open(currentBanner.linkUrl, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = currentBanner.linkUrl
      }
    }
  }

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{ cursor: isDragging ? 'grabbing' : currentBanner.linkUrl ? 'pointer' : 'grab' }}
    >
      <Card
        className={`relative overflow-hidden ${currentBanner.linkUrl ? 'cursor-pointer hover:shadow-lg transition-shadow duration-slow' : ''}`}
        onClick={currentBanner.linkUrl ? handleBannerClick : undefined}
      >
        {/* Banner Image or Carousel */}
        <div className="relative">
          <img
            src={imageUrl}
            alt={currentBanner.title}
            className="w-full object-cover"
            style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement
              // Fallback to placehold.co if image fails to load
              target.src = `https://placehold.co/1200x520/6366f1/ffffff?text=1200x520`
            }}
          />
          {/* Remove image carousel arrows and dots for images */}
        </div>
        {/* Remove navigation arrows for banners */}
      </Card>
      {/* Dots Indicator (if multiple banners) */}
      {visibleBanners.length > 1 && (
        <div className="flex justify-center mt-2 lg:mt-4 space-x-1 lg:space-x-2">
          {visibleBanners.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 lg:w-3 lg:h-3 rounded-full transition-colors ${
                index === currentIndex ? 'bg-primary' : 'bg-muted'
              }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
